import React from "react";
import { Link } from "react-router-dom";
import { createPageStyles } from "./MarketingPageStyles";
import { detectPageLocale } from "./pageLocale";

const styles = createPageStyles("light", "kana");

type Locale = "ko" | "en";

const LOCALE_STORAGE_KEY = "kanatomo-terms-locale";
const locales: Locale[] = ["ko", "en"];
const localeLabels: Record<Locale, string> = { ko: "KO", en: "EN" };

const content: Record<
  Locale,
  {
    crumb: string;
    title: string;
    accentTitle: string;
    subtitle: string;
    sections: Array<{ title: string; body?: string; list?: string[] }>;
  }
> = {
  ko: {
    crumb: "KanaTomo Terms",
    title: "KanaTomo,",
    accentTitle: "이용약관",
    subtitle:
      "최종 업데이트일: 2026년 5월 18일. 본 서비스의 운영 주체는 Daydream Factory입니다. KanaTomo 앱과 관련 웹페이지를 이용하는 경우 아래 약관이 적용됩니다.",
    sections: [
      {
        title: "1. 서비스 소개",
        body:
          "KanaTomo는 일본어 단어 학습, 예문 학습, 복습 및 부가 학습 기능을 제공하는 모바일 애플리케이션과 관련 랜딩 페이지로 구성됩니다.",
      },
      {
        title: "2. 이용 조건",
        list: [
          "사용자는 관련 법령을 위반하거나 서비스 운영을 방해하는 방식으로 서비스를 이용해서는 안 됩니다.",
          "앱 또는 웹사이트의 비정상적 접근, 자동화 남용, 역분석 시도는 제한될 수 있습니다.",
          "스토어 정책 및 기기 플랫폼 정책에 따라 일부 기능 제공 방식이 달라질 수 있습니다.",
        ],
      },
      {
        title: "3. 지식재산권",
        list: [
          "앱 내 텍스트, 디자인, 로고, 화면 구성 등 서비스 관련 권리는 Daydream Factory 또는 정당한 권리자에게 귀속됩니다.",
          "사전 허가 없이 서비스의 일부 또는 전부를 복제, 배포, 상업적으로 이용할 수 없습니다.",
        ],
      },
      {
        title: "4. 서비스 변경 및 중단",
        body:
          "Daydream Factory는 서비스 개선, 운영상 필요, 법적 요구 등에 따라 기능을 변경하거나 일부 서비스를 중단할 수 있습니다. 중요한 변경은 가능한 범위에서 사전에 안내합니다.",
      },
      {
        title: "5. 면책",
        list: [
          "서비스는 제공 가능한 범위 내에서 운영되며, 네트워크 환경이나 외부 플랫폼 사정으로 일부 기능이 제한될 수 있습니다.",
          "사용자의 기기 환경, 설정, 외부 스토어 정책으로 인한 이용 제한에 대해 운영 주체가 항상 책임을 부담하는 것은 아닙니다.",
        ],
      },
      {
        title: "6. 문의",
        body: "약관 관련 문의는 아래 이메일로 접수할 수 있습니다: support@daydreamfactory.net",
      },
    ],
  },
  en: {
    crumb: "KanaTomo Terms",
    title: "KanaTomo,",
    accentTitle: "Terms of Use",
    subtitle:
      "Last updated: May 18, 2026. This service is operated by Daydream Factory. These terms apply to the KanaTomo app and its related web pages.",
    sections: [
      {
        title: "1. Service Overview",
        body:
          "KanaTomo consists of a mobile app and related landing pages that provide Japanese vocabulary study, example-based learning, review flows, and supporting study features.",
      },
      {
        title: "2. Conditions of Use",
        list: [
          "Users must not use the service in ways that violate applicable law or interfere with service operations.",
          "Abnormal access, abusive automation, or reverse-engineering attempts may be restricted.",
          "Some features may vary depending on store rules or device platform policies.",
        ],
      },
      {
        title: "3. Intellectual Property",
        list: [
          "Rights to app text, design, logos, and interface elements belong to Daydream Factory or the respective rights holders.",
          "You may not reproduce, distribute, or commercially use any part of the service without prior permission.",
        ],
      },
      {
        title: "4. Service Changes and Suspension",
        body:
          "Daydream Factory may change features or suspend parts of the service for improvement, operational needs, or legal reasons. Material changes will be announced when reasonably possible.",
      },
      {
        title: "5. Disclaimer",
        list: [
          "The service is provided within available operational limits, and some features may be affected by network conditions or external platform issues.",
          "The operator is not always responsible for limitations caused by user device settings, environments, or third-party store policies.",
        ],
      },
      {
        title: "6. Contact",
        body: "For questions about these terms, contact: support@daydreamfactory.net",
      },
    ],
  },
};

const Terms: React.FC = () => {
  const [locale, setLocale] = React.useState<Locale>("ko");

  React.useEffect(() => {
    setLocale(detectPageLocale(LOCALE_STORAGE_KEY, locales, "ko"));
  }, []);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const copy = content[locale];

  return (
    <main style={styles.page}>
      <div style={styles.shell}>
        <div style={styles.topRow}>
          <Link to="/" style={styles.homeLink}>
            ← Daydream Factory
          </Link>
          <div style={styles.topRowMeta}>
            <div style={styles.localeSwitch} aria-label="Language switcher">
              {locales.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setLocale(item)}
                  style={{
                    ...styles.localeButton,
                    ...(locale === item ? styles.activeLocaleButton : {}),
                  }}
                >
                  {localeLabels[item]}
                </button>
              ))}
            </div>
            <span style={styles.crumb}>{copy.crumb}</span>
          </div>
        </div>

        <section style={styles.hero}>
          <h1 style={styles.title}>
            {copy.title}
            <br />
            <span style={styles.accentTitle}>{copy.accentTitle}</span>
          </h1>
          <p style={styles.subtitle}>{copy.subtitle}</p>
        </section>

        {copy.sections.map((section) => (
          <section key={section.title} style={styles.section}>
            <h2 style={styles.h2}>{section.title}</h2>
            <div style={styles.sectionCard}>
              {section.body ? (
                <p style={styles.body}>
                  {section.body.includes("support@daydreamfactory.net") ? (
                    <>
                      {section.body.replace("support@daydreamfactory.net", "")}
                      <a href="mailto:support@daydreamfactory.net" style={styles.link}>
                        support@daydreamfactory.net
                      </a>
                    </>
                  ) : (
                    section.body
                  )}
                </p>
              ) : null}
              {section.list ? (
                <ul style={styles.list}>
                  {section.list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          </section>
        ))}
      </div>
    </main>
  );
};

export default Terms;
